import { http } from '@/core/api'
import { i18n } from '@/i18n'

export type LeadZaloFriendship = {
  is_friend: boolean
  is_requested: boolean
  is_requesting: boolean
}

export type LeadZaloStoredMessage = {
  id: number
  zalo_msg_id: string
  direction: 'in' | 'out'
  kind: 'text' | 'image' | 'video' | 'file' | 'sticker' | 'link' | string
  body: string | null
  filename: string | null
  preview_url?: string | null
  cli_msg_id?: string | null
  reply_to_message_id?: string | null
  quote?: {
    preview?: string | null
    author_self?: boolean
    msg_id?: string | null
  } | null
  reactions?: Array<{ icon: string; count: number }>
  my_reaction?: string | null
  sent_at: string | null
  send_block?: 'stranger' | null
}

export type LeadZaloDmOpenResult = LeadZaloFriendship & {
  application_id: number | null
  assignment_id: number | null
  worker_id: number | null
  zalo_user_id: string
  display_name: string
  avatar: string | null
  zalo_username: string | null
  zalo_gender: number | null
  zalo_cover_url: string | null
  zalo_status_message: string | null
  zalo_birthday: string | null
  phone_masked: string | null
  bridge_state: string | null
  /** Backend lead thread id (one per lead, several per Zalo person). */
  conversation_id?: number | null
  /** Reclaimed lead, or the Zalo person is now cared for under another lead. */
  read_only?: boolean
  /** Lead was reclaimed from this recruiter. */
  reclaimed?: boolean
  messages?: LeadZaloStoredMessage[]
  history_synced?: number
  /** True while a background job is still resolving the Zalo uid. The HTTP open itself did not wait. */
  peer_pending?: boolean
}

export type LeadZaloDmSendResult = {
  message_id: string | null
  zalo_user_id: string
  display_name: string
  cli_msg_id?: string | null
}

export type LeadZaloDmFileResult = LeadZaloDmSendResult & {
  filename: string
  kind: 'image' | 'video' | 'file'
  preview_url?: string | null
}

export type LeadZaloSticker = {
  id: number
  cate_id: number
  type: number
  text?: string
  sticker_url: string | null
  sticker_sprite_url?: string | null
  total_frames?: number
}

export type LeadZaloDmStickerResult = LeadZaloDmSendResult & {
  kind: 'sticker'
  preview_url?: string | null
}

export type LeadZaloDmLinkResult = LeadZaloDmSendResult & {
  kind: 'link' | 'text'
  link: string
}

export type LeadZaloDmForwardResult = {
  success: Array<Record<string, unknown>>
  failed: Array<Record<string, unknown>>
}

export type LeadZaloDmUndoResult = {
  message_id: string
  deleted: boolean
}

export type LeadZaloFriendRequestResult = LeadZaloFriendship & {
  zalo_user_id: string
  display_name: string
}

export type AssignmentZaloFriendRequestResult = LeadZaloFriendRequestResult & {
  /** false when they were already friends / already had a pending request — no new send happened. */
  sent: boolean
  /** Same vocabulary as the workbench column, incl. `no_zalo` when the phone has no account. */
  zalo_status: 'not_friend' | 'requested' | 'friend' | 'no_zalo'
}

/** Kết quả phân giải tên sau khi đổi tên gợi nhớ — cùng shape với field trên row workbench. */
export type LeadCareNameResult = {
  display_name: string | null
  display_source: 'remembered' | 'worker' | 'zalo_profile' | 'none'
  remembered_name: string | null
  worker_name: string | null
  zalo_profile_name: string | null
  zalo_friend_alias: string | null
}

export type LeadZaloPhoneResult = {
  zalo_phone: string
  zalo_status: 'not_friend' | 'requested' | 'friend'
  display_name: string
  sent: boolean
}

type ZaloPhoneQueued = { queued: true; request_id: string }

type ZaloPhoneRequestState = {
  status: 'pending' | 'done' | 'failed'
  result?: LeadZaloPhoneResult
  message?: string
}

const ZALO_PHONE_POLL_MS = 2_000
/**
 * Follow-check lookups usually finish in 1–5s — first polls stay short so the click feels instant.
 * After that the gap grows; the request still stops at {@link ZALO_PHONE_WAIT_MS} or on done/failed.
 */
const ZALO_CHECK_POLL_STEPS_MS = [300, 500, 700, 1_000, 1_500, 2_000, 3_000, 5_000]
const ZALO_CHECK_POLL_MS = 5_000
/** Job timeout is 50s; stop a little after so a dead worker does not spin the modal forever. */
const ZALO_PHONE_WAIT_MS = 60_000

/** Same caps as backend `zalo_crm_bridge.max_image_bytes` / `max_file_bytes`. */
export const ZALO_MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const ZALO_MAX_FILE_BYTES = 20 * 1024 * 1024

/** Upload percentage 0–100. */
export type UploadProgressHandler = (percent: number) => void

function uploadProgressListener(onProgress?: UploadProgressHandler) {
  if (!onProgress) return undefined
  return (event: { loaded: number; total?: number }) => {
    if (!event.total) return
    onProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)))
  }
}

export const leadZaloDmApi = {
  async open(applicationId: number, zaloUserId?: string, options?: { pulse?: boolean }) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/open`, {
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
      ...(options?.pulse ? { pulse: 1 } : {}),
    })
    return data as { data: LeadZaloDmOpenResult }
  },

  /** The lookup runs on the queue (keeps PHP-FPM free); wait here so callers stay unchanged. */
  async checkForAssignment(assignmentId: number) {
    type CheckResult = {
      has_zalo: boolean
      zalo_user_id: string
      zalo_status: 'not_friend' | 'requested' | 'friend' | 'no_zalo'
    }
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/check`)
    const body = data as { data: CheckResult | ZaloPhoneQueued }
    if (!('queued' in body.data)) return body as { data: CheckResult }

    const requestId = body.data.request_id
    const deadline = Date.now() + ZALO_PHONE_WAIT_MS
    let attempt = 0
    while (Date.now() < deadline) {
      const delay = ZALO_CHECK_POLL_STEPS_MS[attempt] ?? ZALO_CHECK_POLL_MS
      attempt += 1
      await new Promise((resolve) => setTimeout(resolve, delay))
      const { data: statusBody } = await http.get(`/assignments/zalo-phone-requests/${requestId}`)
      const state = (statusBody as {
        data: { status: string; result?: CheckResult; message?: string; http_status?: number; code?: string }
      }).data
      if (state.status === 'done' && state.result) return { data: state.result }
      if (state.status === 'failed') {
        // Same shape as an axios error so callers keep their status checks (404 → add Zalo phone).
        const message = state.message || i18n.global.t('recruitmentCenter.zaloChat.openFailed')
        throw Object.assign(new Error(message), {
          response: { status: state.http_status ?? 422, data: { message, code: state.code } },
        })
      }
    }
    throw new Error(i18n.global.t('leadLibrary.workbench.v2.zaloPhoneLookupSlow'))
  },

  async openForAssignment(assignmentId: number, zaloUserId?: string, options?: { pulse?: boolean }) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/open`, {
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
      ...(options?.pulse ? { pulse: 1 } : {}),
    })
    return data as { data: LeadZaloDmOpenResult }
  },

  async listMessages(
    applicationId: number,
    options?: { afterId?: number; beforeId?: number; limit?: number },
  ) {
    const params: Record<string, number> = {}
    if (options?.afterId && options.afterId > 0) params.after_id = options.afterId
    if (options?.beforeId && options.beforeId > 0) params.before_id = options.beforeId
    if (options?.limit && options.limit > 0) params.limit = options.limit
    const { data } = await http.get(`/recruitment/leads/${applicationId}/zalo-dm/messages`, {
      params: Object.keys(params).length ? params : undefined,
    })
    return data as {
      data: LeadZaloStoredMessage[]
      meta?: { has_more_older?: boolean }
    }
  },

  async messagesForAssignment(
    assignmentId: number,
    options?: { afterId?: number; beforeId?: number; limit?: number },
  ) {
    const params: Record<string, number> = {}
    if (options?.afterId && options.afterId > 0) params.after_id = options.afterId
    if (options?.beforeId && options.beforeId > 0) params.before_id = options.beforeId
    if (options?.limit && options.limit > 0) params.limit = options.limit
    const { data } = await http.get(`/assignments/${assignmentId}/zalo-dm/messages`, {
      params: Object.keys(params).length ? params : undefined,
    })
    return data as {
      data: LeadZaloStoredMessage[]
      meta?: { has_more_older?: boolean }
    }
  },

  async friendStatus(applicationId: number, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/friend-status`, {
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloFriendship & { zalo_user_id: string } }
  },

  async friendStatusForAssignment(assignmentId: number, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/friend-status`, {
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloFriendship & { zalo_user_id: string } }
  },

  async sendFriendRequest(applicationId: number, message = '', zaloUserId?: string) {
    const body: { message?: string; zalo_user_id?: string } = {}
    if (message.trim()) body.message = message.trim()
    if (zaloUserId) body.zalo_user_id = zaloUserId
    const { data } = await http.post(
      `/recruitment/leads/${applicationId}/zalo-dm/friend-request`,
      body,
    )
    return data as { data: LeadZaloFriendRequestResult; message?: string }
  },

  /**
   * "Theo dõi" click on a my-resources row that has no RecruitmentApplication yet — resolves the
   * lead's Zalo account from its worker phone and sends a friend request (idempotent: the backend
   * skips re-sending once they're already friends / already pending).
   */
  async sendFriendRequestForAssignment(assignmentId: number, message = '', zaloUserId?: string) {
    const body: { message?: string; zalo_user_id?: string } = {}
    if (message.trim()) body.message = message.trim()
    if (zaloUserId) body.zalo_user_id = zaloUserId
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/friend-request`, body)
    return data as { data: AssignmentZaloFriendRequestResult; message?: string }
  },

  async undoFriendRequest(applicationId: number, zaloUserId?: string) {
    const { data } = await http.post(
      `/recruitment/leads/${applicationId}/zalo-dm/friend-request/undo`,
      zaloUserId ? { zalo_user_id: zaloUserId } : {},
    )
    return data as { data: LeadZaloFriendRequestResult }
  },

  async undoFriendRequestForAssignment(assignmentId: number, zaloUserId?: string) {
    const { data } = await http.post(
      `/assignments/${assignmentId}/zalo-dm/friend-request/undo`,
      zaloUserId ? { zalo_user_id: zaloUserId } : {},
    )
    return data as { data: AssignmentZaloFriendRequestResult }
  },

  async removeFriend(applicationId: number, zaloUserId?: string) {
    const { data } = await http.post(
      `/recruitment/leads/${applicationId}/zalo-dm/unfriend`,
      zaloUserId ? { zalo_user_id: zaloUserId } : {},
    )
    return data as { data: LeadZaloFriendRequestResult }
  },

  async removeFriendForAssignment(assignmentId: number, zaloUserId?: string) {
    const { data } = await http.post(
      `/assignments/${assignmentId}/zalo-dm/unfriend`,
      zaloUserId ? { zalo_user_id: zaloUserId } : {},
    )
    return data as { data: AssignmentZaloFriendRequestResult }
  },

  /**
   * Đặt/xoá "tên gợi nhớ" của CSKH cho lead. Backend đồng bộ luôn biệt danh Zalo khi đã kết bạn
   * và chỉ trả thành công khi cả hai phía cùng xong — nên UI chỉ được đổi sau response này,
   * không optimistic update.
   */
  async updateCareName(assignmentId: number, rememberedName: string | null) {
    const { data } = await http.patch(`/assignments/${assignmentId}/care-name`, {
      remembered_name: rememberedName,
    })
    return data as { data: LeadCareNameResult; message?: string }
  },

  /** Owner marks «Đã kết bạn Zalo» by hand when the friendship happened outside the system. */
  async markZaloFriend(assignmentId: number, isFriend: boolean) {
    const { data } = await http.patch(`/assignments/${assignmentId}/zalo-friend`, { is_friend: isFriend })
    return data as {
      data: { zalo_status: 'not_friend' | 'requested' | 'friend' | 'no_zalo' | null; zalo_friend_marked: boolean }
    }
  },

  /** The Zalo lookup runs on the queue; wait here for the result so callers stay unchanged. */
  async updateZaloPhone(assignmentId: number, zaloPhone: string) {
    const { data } = await http.patch(`/assignments/${assignmentId}/zalo-phone`, {
      zalo_phone: zaloPhone,
    })
    const body = data as { data: LeadZaloPhoneResult | ZaloPhoneQueued; message?: string }
    if (!('queued' in body.data)) return body as { data: LeadZaloPhoneResult; message?: string }

    const requestId = body.data.request_id
    const deadline = Date.now() + ZALO_PHONE_WAIT_MS
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, ZALO_PHONE_POLL_MS))
      const { data: statusBody } = await http.get(`/assignments/zalo-phone-requests/${requestId}`)
      const state = (statusBody as { data: ZaloPhoneRequestState }).data
      if (state.status === 'done' && state.result) return { data: state.result }
      if (state.status === 'failed') {
        throw new Error(state.message || i18n.global.t('leadLibrary.workbench.v2.zaloPhoneUpdateFailed'))
      }
    }
    throw new Error(i18n.global.t('leadLibrary.workbench.v2.zaloPhoneLookupSlow'))
  },

  async send(
    applicationId: number,
    message: string,
    zaloUserId?: string,
    replyTo?: {
      messageId?: string
      cliMsgId?: string | null
      preview?: string | null
      fromSelf?: boolean | null
      sentAtMs?: number | null
    },
  ) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/messages`, {
      message,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
      ...(replyTo?.messageId ? { reply_to_message_id: replyTo.messageId } : {}),
      ...(replyTo?.cliMsgId ? { reply_to_cli_msg_id: replyTo.cliMsgId } : {}),
      ...(replyTo?.preview ? { reply_to_preview: replyTo.preview } : {}),
      ...(replyTo?.fromSelf != null ? { reply_to_from_self: replyTo.fromSelf } : {}),
      ...(replyTo?.sentAtMs ? { reply_to_ts: replyTo.sentAtMs } : {}),
    })
    return data as { data: LeadZaloDmSendResult; message?: string }
  },

  async sendForAssignment(
    assignmentId: number,
    message: string,
    zaloUserId?: string,
    replyTo?: {
      messageId?: string
      cliMsgId?: string | null
      preview?: string | null
      fromSelf?: boolean | null
      sentAtMs?: number | null
    },
  ) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/messages`, {
      message,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
      ...(replyTo?.messageId ? { reply_to_message_id: replyTo.messageId } : {}),
      ...(replyTo?.cliMsgId ? { reply_to_cli_msg_id: replyTo.cliMsgId } : {}),
      ...(replyTo?.preview ? { reply_to_preview: replyTo.preview } : {}),
      ...(replyTo?.fromSelf != null ? { reply_to_from_self: replyTo.fromSelf } : {}),
      ...(replyTo?.sentAtMs ? { reply_to_ts: replyTo.sentAtMs } : {}),
    })
    return data as { data: LeadZaloDmSendResult; message?: string }
  },

  async sendFile(
    applicationId: number,
    file: File,
    message = '',
    zaloUserId?: string,
    onProgress?: UploadProgressHandler,
  ) {
    const form = new FormData()
    form.append('file', file)
    if (message.trim()) form.append('message', message.trim())
    if (zaloUserId) form.append('zalo_user_id', zaloUserId)
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
      onUploadProgress: uploadProgressListener(onProgress),
    })
    return data as { data: LeadZaloDmFileResult; message?: string }
  },

  async sendFileForAssignment(
    assignmentId: number,
    file: File,
    message = '',
    zaloUserId?: string,
    onProgress?: UploadProgressHandler,
  ) {
    const form = new FormData()
    form.append('file', file)
    if (message.trim()) form.append('message', message.trim())
    if (zaloUserId) form.append('zalo_user_id', zaloUserId)
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
      onUploadProgress: uploadProgressListener(onProgress),
    })
    return data as { data: LeadZaloDmFileResult; message?: string }
  },

  async sendLink(applicationId: number, link: string, message = '', zaloUserId?: string) {
    const body: { link: string; message?: string; zalo_user_id?: string } = { link }
    if (message.trim()) body.message = message.trim()
    if (zaloUserId) body.zalo_user_id = zaloUserId
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/link`, body)
    return data as { data: LeadZaloDmLinkResult; message?: string }
  },

  async sendLinkForAssignment(assignmentId: number, link: string, message = '', zaloUserId?: string) {
    const body: { link: string; message?: string; zalo_user_id?: string } = { link }
    if (message.trim()) body.message = message.trim()
    if (zaloUserId) body.zalo_user_id = zaloUserId
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/link`, body)
    return data as { data: LeadZaloDmLinkResult; message?: string }
  },

  async forward(applicationId: number, message: string, peerUids: string[], reference?: {
    id: string
    ts?: number
    logSrcType?: number
    fwLvl?: number
  }) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/forward`, {
      message,
      peer_uids: peerUids,
      ...(reference?.id ? { reference } : {}),
    })
    return data as { data: LeadZaloDmForwardResult; message?: string }
  },

  async forwardForAssignment(assignmentId: number, message: string, peerUids: string[], reference?: {
    id: string
    ts?: number
    logSrcType?: number
    fwLvl?: number
  }) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/forward`, {
      message,
      peer_uids: peerUids,
      ...(reference?.id ? { reference } : {}),
    })
    return data as { data: LeadZaloDmForwardResult; message?: string }
  },

  async undo(applicationId: number, messageId: string, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/undo`, {
      message_id: messageId,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmUndoResult; message?: string }
  },

  async undoForAssignment(assignmentId: number, messageId: string, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/undo`, {
      message_id: messageId,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmUndoResult; message?: string }
  },

  async deleteForMe(applicationId: number, messageId: string, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/delete`, {
      message_id: messageId,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmUndoResult; message?: string }
  },

  async deleteForMeForAssignment(assignmentId: number, messageId: string, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/delete`, {
      message_id: messageId,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmUndoResult; message?: string }
  },

  async react(applicationId: number, messageId: string, reaction: string, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/react`, {
      message_id: messageId,
      reaction,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as {
      data: {
        message_id: string
        icon: string
        reactions: Array<{ icon: string; count: number }>
      }
      message?: string
    }
  },

  async reactForAssignment(assignmentId: number, messageId: string, reaction: string, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/react`, {
      message_id: messageId,
      reaction,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as {
      data: {
        message_id: string
        icon: string
        reactions: Array<{ icon: string; count: number }>
      }
      message?: string
    }
  },

  async searchStickers(keyword: string, via: 'assignment' | 'lead' = 'assignment') {
    const path = via === 'lead'
      ? '/recruitment/zalo-dm/stickers/search'
      : '/assignments/zalo-dm/stickers/search'
    const { data } = await http.post(path, { keyword: keyword.trim() || 'hi' })
    return data as { data: { keyword: string; stickers: LeadZaloSticker[] } }
  },

  async sendSticker(applicationId: number, sticker: LeadZaloSticker, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/stickers`, {
      id: sticker.id,
      cate_id: sticker.cate_id,
      type: sticker.type || 1,
      sticker_url: sticker.sticker_url || undefined,
      text: sticker.text ?? '',
      total_frames: sticker.total_frames ?? 1,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmStickerResult; message?: string }
  },

  async sendStickerForAssignment(assignmentId: number, sticker: LeadZaloSticker, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/stickers`, {
      id: sticker.id,
      cate_id: sticker.cate_id,
      type: sticker.type || 1,
      sticker_url: sticker.sticker_url || undefined,
      text: sticker.text ?? '',
      total_frames: sticker.total_frames ?? 1,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: LeadZaloDmStickerResult; message?: string }
  },

  async setAlias(applicationId: number, alias: string, zaloUserId?: string) {
    const { data } = await http.post(`/recruitment/leads/${applicationId}/zalo-dm/alias`, {
      alias,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: { zalo_user_id: string; alias: string; display_name: string }; message?: string }
  },

  async clearAlias(applicationId: number, zaloUserId?: string) {
    const { data } = await http.delete(`/recruitment/leads/${applicationId}/zalo-dm/alias`, {
      params: zaloUserId ? { zalo_user_id: zaloUserId } : undefined,
    })
    return data as { data: { zalo_user_id: string; alias: null; display_name: string }; message?: string }
  },

  async setAliasForAssignment(assignmentId: number, alias: string, zaloUserId?: string) {
    const { data } = await http.post(`/assignments/${assignmentId}/zalo-dm/alias`, {
      alias,
      ...(zaloUserId ? { zalo_user_id: zaloUserId } : {}),
    })
    return data as { data: { zalo_user_id: string; alias: string; display_name: string }; message?: string }
  },

  async clearAliasForAssignment(assignmentId: number, zaloUserId?: string) {
    const { data } = await http.delete(`/assignments/${assignmentId}/zalo-dm/alias`, {
      params: zaloUserId ? { zalo_user_id: zaloUserId } : undefined,
    })
    return data as { data: { zalo_user_id: string; alias: null; display_name: string }; message?: string }
  },
}
